import React, { useState, useEffect } from "react";
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  XCircleIcon,
  ArrowPathIcon,
  ClockIcon,
  SignalIcon,
  ChartBarIcon,
  EyeIcon,
  ShieldCheckIcon,
} from "@heroicons/react/24/outline";
import LoadingSpinner from "./LoadingSpinner";

const ConnectionStatusDashboard = ({ accounts, onRefresh, isRefreshing }) => {
  const [expandedMetrics, setExpandedMetrics] = useState(null);

  const getOverallHealth = () => {
    if (accounts.length === 0) return { status: "no-accounts", score: 0 };

    const healthyAccounts = accounts.filter(
      (acc) => acc.status === "active" && acc.token_status === "valid",
    ).length;

    const warningAccounts = accounts.filter(
      (acc) => acc.token_status === "expiring_soon",
    ).length;

    const errorAccounts = accounts.filter(
      (acc) => acc.status === "error" || acc.token_status === "expired",
    ).length;

    const score = (healthyAccounts / accounts.length) * 100;

    if (errorAccounts > 0)
      return {
        status: "error",
        score,
        healthy: healthyAccounts,
        warning: warningAccounts,
        error: errorAccounts,
      };
    if (warningAccounts > 0)
      return {
        status: "warning",
        score,
        healthy: healthyAccounts,
        warning: warningAccounts,
        error: errorAccounts,
      };
    return {
      status: "healthy",
      score,
      healthy: healthyAccounts,
      warning: warningAccounts,
      error: errorAccounts,
    };
  };

  const formatLastSync = (timestamp) => {
    if (!timestamp) return { text: "Never", status: "error" };

    const date = new Date(timestamp);
    const now = new Date();
    const diffMinutes = Math.floor((now - date) / (1000 * 60));

    if (diffMinutes < 5) return { text: "Just now", status: "success" };
    if (diffMinutes < 60)
      return {
        text: `${diffMinutes}m ago`,
        status: diffMinutes < 15 ? "success" : "warning",
      };

    const diffHours = Math.floor(diffMinutes / 60);
    if (diffHours < 24)
      return {
        text: `${diffHours}h ago`,
        status: diffHours < 6 ? "warning" : "error",
      };

    const diffDays = Math.floor(diffHours / 24);
    return { text: `${diffDays}d ago`, status: "error" };
  };

  const getMetricsForAccount = (account) => {
    // Mock metrics - in real implementation, this would come from API
    const baseMetrics = {
      facebook: {
        posts: { value: 24, change: "+12%", trend: "up" },
        engagement: { value: "3.4%", change: "+0.8%", trend: "up" },
        reach: { value: "12.5K", change: "-5%", trend: "down" },
      },
      instagram: {
        posts: { value: 18, change: "+6%", trend: "up" },
        stories: { value: 32, change: "+25%", trend: "up" },
        engagement: { value: "5.2%", change: "+1.2%", trend: "up" },
      },
      twitter: {
        tweets: { value: 45, change: "+18%", trend: "up" },
        impressions: { value: "89.2K", change: "+15%", trend: "up" },
        engagement: { value: "2.8%", change: "+0.3%", trend: "up" },
      },
      linkedin: {
        posts: { value: 8, change: "+2%", trend: "up" },
        views: { value: "5.6K", change: "+22%", trend: "up" },
        engagement: { value: "4.1%", change: "+0.9%", trend: "up" },
      },
    };

    return baseMetrics[account.platform] || {};
  };

  const overallHealth = getOverallHealth();

  const healthConfig = {
    "no-accounts": {
      icon: ChartBarIcon,
      color: "text-gray-400",
      bg: "bg-gray-50",
      border: "border-gray-200",
      title: "No Accounts Connected",
      message: "Connect your social media accounts to start tracking",
    },
    healthy: {
      icon: CheckCircleIcon,
      color: "text-green-600",
      bg: "bg-green-50",
      border: "border-green-200",
      title: "All Systems Healthy",
      message: "All connected accounts are syncing properly",
    },
    warning: {
      icon: ExclamationTriangleIcon,
      color: "text-amber-600",
      bg: "bg-amber-50",
      border: "border-amber-200",
      title: "Attention Required",
      message: "Some accounts need attention or will expire soon",
    },
    error: {
      icon: XCircleIcon,
      color: "text-red-600",
      bg: "bg-red-50",
      border: "border-red-200",
      title: "Connection Issues",
      message: "Some accounts are disconnected or have expired tokens",
    },
  };

  const config = healthConfig[overallHealth.status];
  const HealthIcon = config.icon;

  return (
    <div className="space-y-6">
      {/* Overall Health Status */}
      <div className={`rounded-xl border-2 ${config.border} ${config.bg} p-6`}>
        <div className="flex items-start justify-between">
          <div className="flex items-start space-x-4">
            <HealthIcon className={`h-8 w-8 ${config.color} mt-1`} />
            <div className="flex-1">
              <h2 className="text-lg font-semibold text-gray-900">
                {config.title}
              </h2>
              <p className="text-gray-600 mt-1">{config.message}</p>

              {accounts.length > 0 && (
                <div className="flex items-center space-x-4 mt-3">
                  <div className="flex items-center text-sm">
                    <div className="w-3 h-3 bg-green-500 rounded-full mr-2"></div>
                    <span className="text-gray-700">
                      {overallHealth.healthy} Healthy
                    </span>
                  </div>
                  {overallHealth.warning > 0 && (
                    <div className="flex items-center text-sm">
                      <div className="w-3 h-3 bg-amber-500 rounded-full mr-2"></div>
                      <span className="text-gray-700">
                        {overallHealth.warning} Warning
                      </span>
                    </div>
                  )}
                  {overallHealth.error > 0 && (
                    <div className="flex items-center text-sm">
                      <div className="w-3 h-3 bg-red-500 rounded-full mr-2"></div>
                      <span className="text-gray-700">
                        {overallHealth.error} Error
                      </span>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="flex items-center space-x-3">
            {accounts.length > 0 && (
              <div className="text-right">
                <div className="text-2xl font-bold text-gray-900">
                  {Math.round(overallHealth.score)}%
                </div>
                <div className="text-sm text-gray-500">Health Score</div>
              </div>
            )}

            <button
              onClick={onRefresh}
              disabled={isRefreshing}
              className="bg-white border border-gray-300 rounded-lg p-2 hover:bg-gray-50 disabled:opacity-50 transition-colors"
              title="Refresh all accounts"
            >
              <ArrowPathIcon
                className={`w-5 h-5 text-gray-600 ${isRefreshing ? "animate-spin" : ""}`}
              />
            </button>
          </div>
        </div>
      </div>

      {/* Individual Account Status Grid */}
      {accounts.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {accounts.map((account) => {
            const lastSync = formatLastSync(account.last_sync);
            const metrics = getMetricsForAccount(account);

            const statusConfig = {
              active: {
                icon: CheckCircleIcon,
                color: "text-green-500",
                bg: "bg-green-50",
                text: "Active",
              },
              warning: {
                icon: ExclamationTriangleIcon,
                color: "text-amber-500",
                bg: "bg-amber-50",
                text: "Warning",
              },
              error: {
                icon: XCircleIcon,
                color: "text-red-500",
                bg: "bg-red-50",
                text: "Error",
              },
            };

            const status =
              account.token_status === "expired" || account.status === "error"
                ? "error"
                : account.token_status === "expiring_soon"
                  ? "warning"
                  : "active";

            const StatusIcon = statusConfig[status].icon;

            return (
              <div
                key={account.id}
                className="bg-white rounded-lg border border-gray-200 p-4 hover:shadow-md transition-shadow"
              >
                {/* Account Header */}
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center space-x-3">
                    <div
                      className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                        account.platform === "facebook"
                          ? "bg-facebook"
                          : account.platform === "instagram"
                            ? "bg-gradient-to-r from-purple-500 to-pink-500"
                            : account.platform === "twitter"
                              ? "bg-black"
                              : account.platform === "linkedin"
                                ? "bg-linkedin"
                                : account.platform === "tiktok"
                                  ? "bg-black"
                                  : account.platform === "threads"
                                    ? "bg-black"
                                    : "bg-gray-500"
                      }`}
                    >
                      <span className="text-white text-sm">
                        {account.platform === "facebook"
                          ? "👥"
                          : account.platform === "instagram"
                            ? "📷"
                            : account.platform === "twitter"
                              ? "🐦"
                              : account.platform === "linkedin"
                                ? "💼"
                                : account.platform === "tiktok"
                                  ? "🎵"
                                  : account.platform === "threads"
                                    ? "🧵"
                                    : "📱"}
                      </span>
                    </div>
                    <div>
                      <div className="font-medium text-gray-900 text-sm">
                        {account.platform.charAt(0).toUpperCase() +
                          account.platform.slice(1)}
                      </div>
                      <div className="text-xs text-gray-500">
                        @{account.username}
                      </div>
                    </div>
                  </div>

                  <div
                    className={`px-2 py-1 rounded-full text-xs font-medium ${statusConfig[status].bg} ${statusConfig[status].color}`}
                  >
                    <StatusIcon className="w-3 h-3 inline mr-1" />
                    {statusConfig[status].text}
                  </div>
                </div>

                {/* Sync Status */}
                <div className="grid grid-cols-2 gap-3 mb-3">
                  <div>
                    <div className="flex items-center text-xs text-gray-500 mb-1">
                      <ClockIcon className="w-3 h-3 mr-1" />
                      Last Sync
                    </div>
                    <div
                      className={`text-sm font-medium ${
                        lastSync.status === "success"
                          ? "text-green-600"
                          : lastSync.status === "warning"
                            ? "text-amber-600"
                            : "text-red-600"
                      }`}
                    >
                      {lastSync.text}
                    </div>
                  </div>

                  <div>
                    <div className="flex items-center text-xs text-gray-500 mb-1">
                      <SignalIcon className="w-3 h-3 mr-1" />
                      Quality
                    </div>
                    <div
                      className={`text-sm font-medium ${
                        account.error_count === 0
                          ? "text-green-600"
                          : account.error_count < 3
                            ? "text-amber-600"
                            : "text-red-600"
                      }`}
                    >
                      {account.error_count === 0
                        ? "Excellent"
                        : account.error_count < 3
                          ? "Good"
                          : "Poor"}
                    </div>
                  </div>
                </div>

                {/* Quick Metrics Preview */}
                {Object.keys(metrics).length > 0 && (
                  <div className="border-t border-gray-100 pt-3">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs text-gray-500">
                        Recent Metrics
                      </span>
                      <button
                        onClick={() =>
                          setExpandedMetrics(
                            expandedMetrics === account.id ? null : account.id,
                          )
                        }
                        className="text-xs text-primary-600 hover:text-primary-700"
                      >
                        <EyeIcon className="w-3 h-3 inline mr-1" />
                        {expandedMetrics === account.id ? "Hide" : "View"}
                      </button>
                    </div>

                    {expandedMetrics === account.id ? (
                      <div className="space-y-2">
                        {Object.entries(metrics).map(([key, metric]) => (
                          <div
                            key={key}
                            className="flex justify-between items-center"
                          >
                            <span className="text-xs text-gray-600 capitalize">
                              {key}
                            </span>
                            <div className="text-right">
                              <div className="text-xs font-medium text-gray-900">
                                {metric.value}
                              </div>
                              <div
                                className={`text-xs ${
                                  metric.trend === "up"
                                    ? "text-green-600"
                                    : metric.trend === "down"
                                      ? "text-red-600"
                                      : "text-gray-500"
                                }`}
                              >
                                {metric.change}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="flex space-x-2">
                        {Object.entries(metrics)
                          .slice(0, 3)
                          .map(([key, metric]) => (
                            <div
                              key={key}
                              className="bg-gray-50 rounded px-2 py-1 min-w-0 flex-1"
                            >
                              <div className="text-xs text-gray-500 truncate">
                                {key}
                              </div>
                              <div className="text-xs font-medium text-gray-900 truncate">
                                {metric.value}
                              </div>
                            </div>
                          ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Security Indicator */}
                <div className="border-t border-gray-100 pt-2 mt-3">
                  <div className="flex items-center text-xs text-gray-500">
                    <ShieldCheckIcon className="w-3 h-3 mr-1" />
                    OAuth 2.0 Secured • Data encrypted
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Empty State for Dashboard */}
      {accounts.length === 0 && (
        <div className="text-center py-8">
          <ChartBarIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            No Data to Display
          </h3>
          <p className="text-gray-500 mb-4">
            Connect your social media accounts to see real-time status and
            metrics here.
          </p>
        </div>
      )}
    </div>
  );
};

export default ConnectionStatusDashboard;
